import React from 'react';
import { ProcessStepRisk } from '../../services/ProcessStepService';
import { AlertTriangle, Brain, User } from 'lucide-react';

interface RiskItemProps {
  risk: ProcessStepRisk;
}

export const RiskItem: React.FC<RiskItemProps> = ({ risk }) => {
  const getRiskScoreColor = (score: number | undefined) => {
    if (!score) return 'text-gray-500';
    if (score >= 15) return 'text-red-600';
    if (score >= 9) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getRiskLevel = (score: number | undefined) => {
    if (!score) return 'Unknown';
    if (score >= 15) return 'High';
    if (score >= 9) return 'Medium';
    return 'Low';
  };

  return (
    <div className="p-3 border-l-3 border-[#57C8F4] bg-gray-50 rounded">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="font-medium text-[#221551] text-sm">
            {risk.risk_id || `RISK-${risk.id}`}
          </div>
          {risk.ai_identified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#57C8F4] text-white text-xs rounded">
              <Brain className="h-3 w-3" />
              AI
            </span>
          )}
          {!risk.ai_identified && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#9D7AB8] text-white text-xs rounded">
              <User className="h-3 w-3" />
              Manual
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" />
          <span className={`text-sm font-medium ${getRiskScoreColor(risk.inherent_risk_score)}`}>
            {getRiskLevel(risk.inherent_risk_score)}
          </span>
        </div>
      </div>

      {risk.risk_taxonomy_path && (
        <div className="text-xs text-[#454F83] italic mb-1">
          {risk.risk_taxonomy_path}
        </div>
      )}

      <div className="text-sm text-[#221551] font-medium mb-1">
        {risk.risk_name}
      </div>

      <div className="text-xs text-gray-600 mb-2 line-clamp-2">
        {risk.risk_description}
      </div>

      <div className="grid grid-cols-2 gap-4 text-xs">
        <div>
          <span className="font-medium text-[#454F83]">Inherent:</span>
          <span className={`ml-1 ${getRiskScoreColor(risk.inherent_risk_score)}`}>
            {risk.inherent_likelihood}×{risk.inherent_impact} = {risk.inherent_risk_score}
          </span>
        </div>
        {risk.residual_risk_score && (
          <div>
            <span className="font-medium text-[#454F83]">Residual:</span>
            <span className={`ml-1 ${getRiskScoreColor(risk.residual_risk_score)}`}>
              {risk.residual_likelihood}×{risk.residual_impact} = {risk.residual_risk_score}
            </span>
          </div>
        )}
      </div>

      {risk.ai_confidence_score && (
        <div className="text-xs text-gray-500 mt-1">
          AI Confidence: {risk.ai_confidence_score}%
        </div>
      )}
    </div>
  );
};